<template>
  <div>
    <div
      v-if="showPopup"
      class="fixed-top w-100 h-100 d-flex align-items-center justify-content-center z-index-50 bg-black bg-opacity-50"
      @click="hidePopup"
    >
      <NuxtLink
        :to="popupLink"
        @click="onPopupLinkClick"
        class="position-relative z-20 d-flex justify-content-center popup-holder mx-auto"
      >
        <NuxtImg
          format="webp"
          :src="img(popupImageUrl)"
          :alt="popupData.title || 'popup'"
          class="img-fluid rounded-lg img-popup shadow-xl pointer"
          loading="lazy"
          placeholder
        />
        <div
          @click.stop.prevent="closePopup"
          class="position-absolute bg-white close-btn pointer text-black"
        >
         <p class="m-0">
          &times;
         </p>
        </div>
      </NuxtLink>
    </div>
  </div>
</template>


<script>
// The popup is the event marked both active and popup in the panel. Its
// `photos` can hold one image per language; the description says which, as
// one language list per photo in the same order, e.g. [["en"], ["tr"]].
// Without that (or with no photo for the current language) the cover is shown.
// Once it's closed with × or its link is followed, it stays hidden in that
// browser for 3 days; otherwise it shows again on every visit. A different image (a new popup
// from the panel) is shown right away.
const POPUP_STORAGE_KEY = "popupLastShown";
const POPUP_INTERVAL_MS = 3 * 24 * 60 * 60 * 1000;

export default {
  data() {
    return {
      showPopup: false,
      popupImageUrl: "",
      popupData: {},
    };
  },
  computed: {
    language() {
      return this.$i18n.locale;
    },
    // redirect_to is where the popup leads: a page path like "events" (gets
    // the /en prefix in English) or a full URL. Empty: clicking only closes it.
    popupLink() {
      const target = (this.popupData.redirect_to || "").trim();
      if (!target || /^https?:\/\//.test(target)) return target;
      const path = `/${target.replace(/^\/+/, "")}`;
      return this.language === "en" && !/^\/en(\/|$)/.test(path)
        ? `/en${path}`
        : path;
    },
  },
  methods: {
    img(photo) {
      return `https://api2.salamisbayconti.com/storage/${photo}`;
    },
    async getPopup() {
      try {
        const res = await $fetch(
          `https://api2.salamisbayconti.com/api/general/${this.language}/event`
        );
        const event = Array.isArray(res)
          ? res.find((e) => e.is_active === 1 && e.popup === 1)
          : null;
        if (!event) return;
        const key = this.popupKey(event);
        if (!this.isPopupDue(key)) return;

        this.popupData = event;
        this.popupImageUrl = this.popupImageFor(event);
        this.showPopup = true;
      } catch (e) {
        console.error("[Popup] fetch error:", e);
      }
    },
    popupImageFor(event) {
      try {
        const photos = JSON.parse(event.photos);
        const langs = JSON.parse(event.description);
        const i = langs.findIndex((l) => [].concat(l).includes(this.language));
        if (i !== -1 && photos[i]) return photos[i];
      } catch {
        // Description isn't a language list: fall back to the cover.
      }
      return event.cover;
    },
    // Same in both languages (cover and photos aren't translated), so seeing
    // the popup in one language counts for the other too.
    popupKey(event) {
      return [event.id, event.cover, event.photos].join("|");
    },
    isPopupDue(key) {
      try {
        const last = JSON.parse(localStorage.getItem(POPUP_STORAGE_KEY));
        return (
          !last ||
          last.key !== key ||
          Date.now() - last.at >= POPUP_INTERVAL_MS
        );
      } catch {
        return true;
      }
    },
    markPopupShown(key) {
      try {
        localStorage.setItem(
          POPUP_STORAGE_KEY,
          JSON.stringify({ key, at: Date.now() })
        );
      } catch {
        // Storage blocked (private mode etc.): the popup just shows every visit.
      }
    },
    hidePopup() {
      this.showPopup = false;
    },
    closePopup() {
      this.hidePopup();
      this.markPopupShown(this.popupKey(this.popupData));
    },
    onPopupLinkClick() {
      // Without redirect_to the image isn't a link, so a click there only
      // closes the popup (like the backdrop) and isn't remembered.
      if (this.popupLink) this.closePopup();
    },
  },
  mounted() {
    this.getPopup();
  },
};
</script>

<style scoped>
.close-btn {
  top: 10px;
  width: 22px;
  right: 30px !important;
  border-radius: 100%;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  line-height: 1;
  font-weight: bold;
}
.popup-holder {
  height: fit-content;
}
.img-popup {
  width: auto;
  max-width: min(400px, 90vw);
  max-height: 85vh;
  height: auto;
}
</style>
